import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  GraduationCap, Search, Phone, Mail, ChevronDown, ChevronUp, User, Trash2, MapPin, Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import RoleBadges from '../components/RoleBadges';

const CentreDropdown = ({ currentCentre, centres, onAssign, rpId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid={`centre-dropdown-${rpId}`}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`px-3 py-1 text-xs font-medium rounded-full flex items-center gap-1 cursor-pointer transition-all ${
          currentCentre
            ? 'bg-teal-100 text-teal-700 hover:bg-teal-200'
            : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
        }`}
        data-testid={`centre-badge-${rpId}`}
      >
        <MapPin className="h-3 w-3" />
        {currentCentre || 'Non assigné'}
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg min-w-[200px] py-1" data-testid={`centre-menu-${rpId}`}>
          <button
            onClick={(e) => { e.stopPropagation(); onAssign(rpId, ''); setOpen(false); }}
            className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${!currentCentre ? 'text-amber-700 font-medium bg-amber-50' : 'text-slate-600'}`}
            data-testid={`assign-none-${rpId}`}
          >
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            Non assigné
          </button>
          {centres.map(c => (
            <button
              key={c.nom}
              onClick={(e) => { e.stopPropagation(); onAssign(rpId, c.nom); setOpen(false); }}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-teal-50 flex items-center gap-2 ${currentCentre === c.nom ? 'text-teal-700 font-medium bg-teal-50' : 'text-slate-600'}`}
              data-testid={`assign-${c.nom}-${rpId}`}
            >
              <span className="w-2 h-2 rounded-full bg-teal-500" />
              {c.nom}
              {currentCentre === c.nom && <span className="ml-auto text-xs text-teal-500">(actuel)</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ListeRP = ({ user }) => {
  const [rps, setRps] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRP, setExpandedRP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [centres, setCentres] = useState([]);

  useEffect(() => {
    loadRPs();
    loadCentres();
  }, []);

  const loadRPs = async () => {
    try {
      const response = await axios.get('/responsables-pedagogiques-list');
      setRps(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadCentres = async () => {
    try {
      // D'abord essayer la collection centres
      const response = await axios.get('/centres');
      if (response.data && response.data.length > 0) {
        setCentres(response.data);
      } else {
        // Fallback sur centre_profiles (sites dynamiques)
        const profilesRes = await axios.get('/centres-profiles');
        const sites = Object.keys(profilesRes.data || {}).map(nom => ({ nom }));
        setCentres(sites);
      }
    } catch { /* ignore */ }
  };

  const handleAssignCentre = async (userId, centreNom) => {
    try {
      await axios.put(`/responsables-pedagogiques/${userId}/centre`, { centre_nom: centreNom });
      toast.success(`Centre assigné : ${centreNom || 'Aucun'}`);
      setRps(prev => prev.map(rp =>
        rp.user_id === userId ? { ...rp, rp_profile: { ...rp.rp_profile, centre_nom: centreNom } } : rp
      ));
    } catch {
      toast.error('Erreur assignation centre');
    }
  };

  const handleDeleteRP = async (userId, rpNom) => {
    if (!window.confirm(`Supprimer le RP ${rpNom} ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`/responsables-pedagogiques/${userId}/delete`);
      toast.success('RP supprimé');
      setRps(prev => prev.filter(rp => rp.user_id !== userId));
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredRPs = rps.filter(rp => {
    const nom = rp.nom || '';
    const prenom = rp.prenom || '';
    const email = rp.email || '';
    return `${nom} ${prenom} ${email}`.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="liste-rp-page">
      <div>
        <h1 className="text-3xl font-display font-bold text-[#0F172A]">Responsables Pédagogiques</h1>
        <p className="text-slate-600">Gestion et assignation des centres</p>
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text" placeholder="Rechercher un RP..." value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
          data-testid="search-rp"
        />
      </div>

      {/* Stats */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-center gap-4">
        <GraduationCap className="h-8 w-8 text-teal-600" />
        <div>
          <p className="text-2xl font-bold text-teal-700">{rps.length}</p>
          <p className="text-sm text-teal-600">Responsable(s) Pédagogique(s)</p>
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-4">
        {filteredRPs.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <GraduationCap className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucun responsable pédagogique trouvé</p>
          </div>
        ) : (
          filteredRPs.map((rp) => {
            const isExpanded = expandedRP === rp.user_id;
            const fullName = `${rp.prenom || ''} ${rp.nom || ''}`.trim() || 'RP';
            const centreNom = rp.rp_profile?.centre_nom || '';

            return (
              <div key={rp.user_id} className="bg-white border border-slate-200 rounded-xl hover:shadow-lg transition-all" data-testid={`rp-${rp.user_id}`}>
                {/* En-tête */}
                <div className="p-4 cursor-pointer hover:bg-slate-50" onClick={() => setExpandedRP(isExpanded ? null : rp.user_id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {(rp.rp_profile?.photo || rp.photo_identite || rp.photo) ? (
                        <img src={rp.rp_profile?.photo || rp.photo_identite || rp.photo} alt={fullName}
                          className="w-14 h-14 rounded-full object-cover flex-shrink-0 border-2 border-teal-500" data-testid={`rp-photo-${rp.user_id}`} />
                      ) : (
                        <div className="w-14 h-14 rounded-full overflow-hidden bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white text-xl font-bold">
                          {fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-lg text-[#0F172A]">{fullName}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          {rp.email && (
                            <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {rp.email}</span>
                          )}
                          {rp.telephone && (
                            <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {rp.telephone}</span>
                          )}
                        </div>
                        {user?.role === 'centre' && (
                          <RoleBadges userId={rp.user_id} roles={rp.roles || ['responsable_pedagogique']} onRolesChange={(newRoles) => {
                            setRps(prev => prev.map(r => r.user_id === rp.user_id ? { ...r, roles: newRoles } : r));
                          }} />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <CentreDropdown
                        currentCentre={centreNom}
                        centres={centres}
                        onAssign={handleAssignCentre}
                        rpId={rp.user_id}
                      />
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteRP(rp.user_id, fullName); }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                    </div>
                  </div>
                </div>

                {/* Détails dépliés */}
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-gradient-to-b from-teal-50 to-white p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Profil */}
                      <div className="bg-white p-5 rounded-xl border border-teal-200 shadow-sm">
                        <h4 className="font-semibold text-teal-800 mb-4 flex items-center gap-2">
                          <User className="h-5 w-5" /> Profil
                        </h4>
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Nom</span>
                            <span className="font-medium">{rp.nom || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Prénom</span>
                            <span className="font-medium">{rp.prenom || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Email</span>
                            <span className="font-medium">{rp.email || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Téléphone</span>
                            <span className="font-medium">{rp.telephone || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Statut</span>
                            <span className="font-medium capitalize">{rp.statut || 'actif'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Assignation centre */}
                      <div className="bg-white p-5 rounded-xl border border-teal-200 shadow-sm">
                        <h4 className="font-semibold text-teal-800 mb-4 flex items-center gap-2">
                          <Building2 className="h-5 w-5" /> Centre Assigné
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <p className="text-sm text-slate-500 mb-2">Centre actuel :</p>
                            <p className="text-lg font-bold text-teal-700">{centreNom || 'Aucun centre assigné'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Changer de centre :</label>
                            <div className="space-y-2">
                              {centres.map(c => (
                                <button
                                  key={c.nom}
                                  onClick={() => handleAssignCentre(rp.user_id, c.nom)}
                                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm transition-all border ${
                                    centreNom === c.nom
                                      ? 'bg-teal-100 border-teal-400 text-teal-800 font-medium'
                                      : 'bg-slate-50 border-slate-200 hover:bg-teal-50 hover:border-teal-300 text-slate-700'
                                  }`}
                                  data-testid={`assign-centre-${c.nom}`}
                                >
                                  <MapPin className="h-4 w-4 inline mr-2" />
                                  {c.nom}
                                  {centreNom === c.nom && <span className="ml-2 text-xs">(actuel)</span>}
                                </button>
                              ))}
                              {centres.length === 0 && <p className="text-xs text-slate-400">Aucun centre disponible</p>}
                              {centreNom && (
                                <button
                                  onClick={() => handleAssignCentre(rp.user_id, '')}
                                  className="w-full text-left px-4 py-2.5 rounded-lg text-sm border border-red-200 text-red-600 hover:bg-red-50"
                                  data-testid="unassign-centre"
                                >
                                  <Trash2 className="h-4 w-4 inline mr-2" /> Retirer l'assignation
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ListeRP;
